// Modules
import React from "react";
// @ts-ignore
import {LocalizationProvider} from 'react-localize';
// Styles
import "./App.sass";
// Services
import LocalizationService from "Services/localization.service";
import {handle401Error, OWNER_NAME} from "Services/service.utils";
// Components
import BookMyHoliday from "./Components/Pages/book-my-holiday";
import ApplyYourPoints from "./Components/Pages/ApplyYourPoints";
import MvLoader from "Generic/MvLoader/MvLoader";

// Constants
import {ScreenType} from "Constants";
// Other
import log, {configApiCalls, EXEC_MODE, INTEGRATION_MODE, loadEnvVariables, setupLogLevel} from "cfg";
import Summary from "./Components/Pages/Summary";
import {Reservations} from "Components/Pages/Reservations";
import {ViewReservation} from "Components/Pages/ViewReservation";
import {ReservationType} from "Interfaces/GetReservations";
import Header from "./Components/Header/Header";
import Footer from "./Components/Footer/Footer";
import { WaitlistManagement } from "Components/Pages/WaitlistManagement";

// @ts-ignore
import sessionStorage from "store/storages/sessionStorage";
import ReservationService from "./Services/reservation.service";
import {ReactAppMode} from "./Interfaces/ReactAppModes";
import { TransactionHistory } from "Components/Pages/TransactionHistory";
import { EnhancedSegment } from "Interfaces/Segment";
import {ReactIntegrationMode} from "./Interfaces/ReactIntegrationMode";
import { ArrivingGuestInfo } from "Components/Pages/ArrivingGuestInfo";
import { GuestInfoConfirmation } from "Components/Pages/ArrivingGuestInfo/Confirmation";
import { TransferPoints } from "Components/Pages/TransferPoints";
import { TransferPointConfirmation } from "Components/Pages/TransferPointConfirmation";
import { CancelReservation } from "Components/Pages/CancelReservation/CancelReservation";

type AppState = {
    lang: string;
    messages: any;
    langBundleLoaded: boolean;
    screenType: ScreenType;
    aypResId: number;
    resType: ReservationType;
    waitListId?: number;
    editingSegment: EnhancedSegment | null;
}
type AppProps = {
    lang: string;
    apiUrl: string;
    checkEligibilityUrl: string;
    apiResUrl: string;
    apiAutoAdjustUrl: string;
    apiBookUrl: string;
    apiAds: string;
    apiUserContractsUrl: string;
    jahiaContentApiUrl: string;
    jahiaRoomsApiUrl: string;
    aypUrl: string;
    ownerPropertiesUrl: string;
    reservationDetailUrl: string;
    reservationDetailUrlWeeks: string;
    apiOwnerProfileUrl: string;
    apiTimeout: string;
    logLevel: string;
    screenType: ScreenType;
    aypResId: number;
    waitListId?: number;
    apiResListUrl: string;
    apiResWaitListUrl: string;
    apiWaitlistUrl: string;
    apiCountryListUrl: string;
    resType: ReservationType;
    apiPointsCalculatorUrl: string;
    apiTransListUrl: string;
    apiWaitlistCreateUrl: string;
    transferPointsContractUrl: string;
    integrationMode: string;
    pointsSearch ?: string;
    resortLimit ?: string;
    guestTermsUrl: string;
}

class App extends React.Component<AppProps, AppState> {
    localizationService: LocalizationService;
    _isMounted: boolean;

    constructor(props: Readonly<AppProps>) {
        super(props);
        this.state = {
            lang: props.lang,
            messages: {},
            langBundleLoaded: false,
            screenType: props.screenType,
            aypResId: props.aypResId,
            resType: props.resType,
            waitListId: props.waitListId,
            editingSegment: null
        };

        loadEnvVariables();
        setupLogLevel(props);
        configApiCalls(props);

        this._isMounted = false;
        this.localizationService = new LocalizationService();
    }

    /**
     * Set the screen type (currently APPLY_POINTS or SEARCH_RESORTS).
     *
     * We render different screens in different jsp's on VCAP
     *
     * Note: This function is only called in development, as the different
     * screens are rendered in different JSP's in production
     */
    setScreenType = (screenType: ScreenType, aypResId?: number, waitListId?: number, resType?: ReservationType) => {
        const state: AppState = {...this.state};

        // If navigating to the AYP page, a reservedInventoryID will be passed
        if (aypResId && aypResId > -1) {
            state.aypResId = aypResId;
            state.waitListId = -1;
        }

        // 1C code Condition Waitlist
        if (waitListId && waitListId > -1) {
            state.waitListId = waitListId;
            state.aypResId = -1;
        }

        //if navigating to view reservation details, reservation type will be passed
        if (resType) {
            state.resType = resType;
        }

        // Update the current screen type
        state.screenType = screenType;

        this.setState(state);
    }

    langSelectionChanged = (newLang: string) => {
        let state: AppState = this.state;
        state.lang = newLang;
        this.setState(state);
    }

    getJahiaContentBundle(lang: string) {
        const { localize } = this.context;
        this.localizationService.getJahiaContentBundle(lang)
            .then((results: any) => {
                let state: AppState = this.state;
                state.messages = results;
                state.langBundleLoaded = true;
                log.debug(`Lang bundle: ${JSON.stringify(state.messages)}`);
                this.setState(state);
            })
            .catch((error: any) => {
                // const errMsg = error.message ? `${error.message}` : "Unspecified error";
                const errMsg = error.message ? `${error.message}` : localize('static.content.pwas-search-fatal-error-message');
                if (!handle401Error(error)) {
                    log.error(`Error: ${JSON.stringify(error)}`);
                }
            });
    }

    componentDidMount() {
        this._isMounted = true;
        this.getJahiaContentBundle(this.props.lang);
        console.log('Deployment Completed');
    }

    componentWillUnmount(): void {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps: Readonly<AppProps>) {
        if (this.props.lang !== prevProps.lang) {
            this.getJahiaContentBundle(this.props.lang);
        }
    }

    setEditingSegment = (editingSegment: EnhancedSegment) => {
        let state: AppState = this.state;
        state.editingSegment = editingSegment;
        this.setState(state);
    }

    render() {
        return (
            // <div className="pageContainer col-xs-12 col-md-10 col-md-offset-1 noPadding">
            // <div className="appContainer">

            <MvLoader show={!this.state.langBundleLoaded}>
                {/* { EXEC_MODE === ReactAppMode.VOA && INTEGRATION_MODE === ReactIntegrationMode.VOA &&
                <div className="headerAppContainer">
                    <Header lang={this.props.lang}
                            ownerName={sessionStorage.read(OWNER_NAME)}
                            ownerBenefitLevel={sessionStorage.read(ReservationService.BENEFIT_LEVEL)}/>
                </div>
                } */}
                <LocalizationProvider messages={this.state.messages}>
                    {this.state.screenType === ScreenType.SEARCH_RESORTS &&
                    <BookMyHoliday
                        token={""}
                        lang={this.state.lang}
                        setScreenType={this.setScreenType}
                        setEditingSegment={this.setEditingSegment}
                        editingSegment={this.state.editingSegment}
                        resortLimit = {this.props.resortLimit}
                        apiTimeout = {this.props.apiTimeout}
                        pointsSearch = {this.props.pointsSearch}
                    />
                    }
                    {this.state.screenType === ScreenType.APPLY_POINTS &&
                    <ApplyYourPoints
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        setScreenType={this.setScreenType}
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {this.state.screenType === ScreenType.SUMMARY_REVIEW &&
                    <Summary
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        setScreenType={this.setScreenType}
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {this.state.screenType === ScreenType.CANCEL_RESERVATION &&
                    <CancelReservation
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        resType={this.state.resType}
                        setScreenType={this.setScreenType}
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {this.state.screenType === ScreenType.WAITLIST_CANCEL_SUMMARY_REVIEW &&
                    <Summary
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        setScreenType={this.setScreenType}
                        waitListId={this.state.waitListId}
                        screenType={this.state.screenType }
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {this.state.screenType === ScreenType.WAITLIST_SUMMARY_REVIEW &&
                    <Summary
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        setScreenType={this.setScreenType}
                        waitListId={this.state.waitListId}
                        screenType={this.state.screenType }
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }

                    {this.state.screenType === ScreenType.WAITLIST_MANAGEMENT &&
                    <WaitlistManagement
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        setScreenType={this.setScreenType}
                        screenType={this.state.screenType}
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {this.state.screenType === ScreenType.RESERVATIONS &&
                    <Reservations
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        setScreenType={this.setScreenType}
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {this.state.screenType === ScreenType.VIEW_RESERVATION &&
                    <ViewReservation
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        resType={this.state.resType}
                        setScreenType={this.setScreenType}
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {this.state.screenType === ScreenType.TRANSACTION_HISTORY &&
                    <TransactionHistory
                        token={""}
                        lang={this.state.lang}
                        resId={this.state.aypResId}
                        setScreenType={this.setScreenType}
                        apiTimeout = {this.props.apiTimeout}
                    />
                    }
                    {
                        this.state.screenType === ScreenType.TRANSFER_POINTS &&
                        <TransferPoints 
                            token={""}
                            lang={this.state.lang}
                            setScreenType={this.setScreenType}
                        />
                    }
                    {
                        this.state.screenType === ScreenType.TRANSFER_POINTS_CONFIRMATION &&
                        <TransferPointConfirmation 
                            token={""}
                            lang={this.state.lang}
                            setScreenType={this.setScreenType}
                        />
                    }
                    {
                        this.state.screenType === ScreenType.ARRIVING_GUEST_INFO &&
                        <ArrivingGuestInfo 
                            token={""}
                            resId={this.state}
                            lang={this.state.lang}
                            setScreenType={this.setScreenType}
                        />
                    }
                    {
                        this.state.screenType === ScreenType.GUEST_INFO_CONFIRMATION &&
                        <GuestInfoConfirmation 
                            token={""}
                            lang={this.state.lang}
                            setScreenType={this.setScreenType}
                        />
                    }
                </LocalizationProvider>
                {EXEC_MODE === ReactAppMode.VOA && INTEGRATION_MODE === ReactIntegrationMode.VOA &&
                <div className="footerAppContainer">
                    <Footer {...this.state}/>
                </div>
                }
            </MvLoader>
            // </div>
            //  </div>
        );
    }
}

export default App;
